import React, { useState, useEffect } from 'react';
import { Check, X, FileText, User, Calendar, MessageSquare, Loader2, AlertTriangle, Clock } from 'lucide-react';
import api from '../../services/api';

/**
 * Document Share Approval Queue
 * Shows pending document share requests for attorney approval
 */
const DocumentApprovalQueue = ({ caseId = null, onApprovalChange }) => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);

  useEffect(() => {
    loadPending();
  }, [caseId]);

  const loadPending = async () => {
    setLoading(true);
    try {
      const params = caseId ? { case_id: caseId } : {};
      const response = await api.get('/documents/shares/pending', { params });
      setPending(response.data);
    } catch (err) {
      console.error('Error loading pending approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (shareId) => {
    setProcessing(shareId);
    try {
      await api.post(`/documents/shares/${shareId}/approve`);
      setPending(prev => prev.filter(p => p.id !== shareId));
      if (onApprovalChange) onApprovalChange();
    } catch (err) {
      console.error('Error approving share:', err);
      alert(err.response?.data?.detail || 'Failed to approve share');
    } finally {
      setProcessing(null);
    }
  };

  const handleBulkApprove = async () => {
    if (!window.confirm(`Approve all ${pending.length} pending shares?`)) return;

    setBulkProcessing(true);
    try {
      const response = await api.post('/documents/shares/bulk-approve');
      setPending([]);
      if (onApprovalChange) onApprovalChange();
      alert(`Successfully approved ${response.data.approved_count} shares`);
    } catch (err) {
      console.error('Error bulk approving:', err);
      alert(err.response?.data?.detail || 'Failed to bulk approve shares');
      loadPending(); // Reload to show what's left
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleReject = async (shareId) => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setProcessing(shareId);
    try {
      await api.post(`/documents/shares/${shareId}/reject`, {
        reason: rejectReason
      });
      setPending(prev => prev.filter(p => p.id !== shareId));
      setShowRejectModal(null);
      setRejectReason('');
      if (onApprovalChange) onApprovalChange();
    } catch (err) {
      console.error('Error rejecting share:', err);
      alert(err.response?.data?.detail || 'Failed to reject share');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="animate-spin text-blue-500 mr-2" size={20} />
        <span className="text-slate-400">Loading pending approvals...</span>
      </div>
    );
  }

  if (pending.length === 0) {
    return (
      <div className="text-center p-8 text-slate-400">
        <Check className="mx-auto mb-3 text-green-500" size={32} />
        <p>No pending document share approvals</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Clock className="text-amber-400" size={20} />
          Pending Approvals
          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-sm">
            {pending.length}
          </span>
        </h3>
        {pending.length > 1 && (
          <button
            onClick={handleBulkApprove}
            disabled={bulkProcessing}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-sm rounded transition-colors disabled:opacity-50"
          >
            {bulkProcessing ? (
              <Loader2 className="animate-spin" size={14} />
            ) : (
              <Check size={14} />
            )}
            Approve All
          </button>
        )}
      </div>

      {pending.map(item => (
        <div
          key={item.id}
          className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Document info */}
              <div className="flex items-center gap-2 mb-2">
                <FileText className="text-blue-400" size={18} />
                <span className="text-white font-medium">{item.document_name}</span>
              </div>

              {/* Client info */}
              <div className="flex items-center gap-2 text-sm text-slate-400 mb-1">
                <User size={14} />
                <span>
                  {item.requested_by_client ? (
                    <span className="text-cyan-400">
                      Client request from <strong>{item.contact_name}</strong>
                    </span>
                  ) : (
                    <span>
                      Share to <strong>{item.contact_name}</strong> by {item.shared_by}
                    </span>
                  )}
                </span>
              </div>

              {/* Case info */}
              {item.case_name && (
                <div className="text-sm text-slate-500 mb-1">
                  Case: {item.case_name}
                </div>
              )}

              {/* Request message */}
              {item.request_message && (
                <div className="mt-2 p-2 bg-slate-900/50 rounded text-sm">
                  <div className="flex items-start gap-2">
                    <MessageSquare size={14} className="text-slate-400 mt-0.5" />
                    <span className="text-slate-300 italic">"{item.request_message}"</span>
                  </div>
                </div>
              )}

              {/* Expiration */}
              {item.expires_at && (
                <div className="flex items-center gap-1 text-xs text-amber-400 mt-2">
                  <Calendar size={12} />
                  <span>Expires: {new Date(item.expires_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={() => handleApprove(item.id)}
                disabled={processing === item.id}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded transition-colors disabled:opacity-50"
              >
                {processing === item.id ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <Check size={16} />
                )}
                Approve
              </button>
              <button
                onClick={() => setShowRejectModal(item.id)}
                disabled={processing === item.id}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded transition-colors disabled:opacity-50"
              >
                <X size={16} />
                Reject
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-md mx-4 border border-slate-700">
            <div className="p-4 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="text-red-400" size={20} />
                Reject Document Share
              </h3>
            </div>
            <div className="p-4">
              <p className="text-slate-300 mb-4">
                Please provide a reason for rejecting this share request:
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection..."
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-700">
              <button
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReject(showRejectModal)}
                disabled={!rejectReason.trim() || processing}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {processing ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <X size={16} />
                )}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentApprovalQueue;
